/**
 * Tenant Dashboard — Platform-wide tenant stats overview.
 * Shows status metric cards and recent cross-tenant activity feed.
 *
 * @see Documentation/Specification/020_Definitions/application-agnostic/identity/Tenant.md
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-admin';
import {
  Box, Typography, Paper, Grid, Avatar, Skeleton,
} from '@mui/material';
import { Business, ArrowForward } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { STATUS_COLORS } from './types';

interface TenantStats {
  totalTenants: number;
  activeTenants: number;
  suspendedTenants: number;
  liquidatedTenants: number;
  pendingVerification: number;
  recentActivity: Array<{ action: string; tenantId: string; timestamp: string }>;
}

export const TenantDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<TenantStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ctrl = new AbortController();
    fetch('/api/v1/tenants/stats', { signal: ctrl.signal })
      .then(r => r.json())
      .then(json => setStats(json.data ?? json))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rounded" height={80} sx={{ mb: 2, borderRadius: 1 }} />
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map(i => (
            <Grid key={i} size={{ xs: 6, sm: 3 }}>
              <Skeleton variant="rounded" height={90} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!stats) return null;

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Tenant Dashboard"
        action={
          <Box
            component="button"
            onClick={() => navigate('/tenants')}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              bgcolor: 'primary.main', color: 'white', border: 'none', borderRadius: 1,
              px: 2, py: 1, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            View All Tenants <ArrowForward sx={{ fontSize: 16 }} />
          </Box>
        }
        metrics={[
          { label: 'Total', value: stats.totalTenants, color: 'primary.main' },
          { label: 'Active', value: stats.activeTenants, color: 'success.main' },
          { label: 'Suspended', value: stats.suspendedTenants, color: 'warning.main' },
          { label: 'Liquidated', value: stats.liquidatedTenants, color: 'grey.600' },
        ]}
      />

      {stats.pendingVerification > 0 && (
        <Paper sx={{ p: 2, mb: 2, borderLeft: '4px solid', borderColor: 'info.main', bgcolor: 'info.lighter' }}>
          <Typography variant="body2" fontWeight={600} color="info.dark">
            {stats.pendingVerification} tenant{stats.pendingVerification !== 1 ? 's' : ''} pending verification
          </Typography>
        </Paper>
      )}

      {stats.recentActivity.length > 0 && (
        <Paper sx={{ overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={600}>Recent Activity</Typography>
          </Box>
          <Box>
            {stats.recentActivity.map((entry, i) => (
              <Box
                key={`${entry.tenantId}-${i}`}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1,
                  borderBottom: i < stats.recentActivity.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: 11 }}>
                  <Business sx={{ fontSize: 14 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={500}>{entry.action}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tenant: {entry.tenantId}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {new Date(entry.timestamp).toLocaleDateString()}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {stats.recentActivity.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Business sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
          <Typography color="text.secondary">No recent activity</Typography>
        </Paper>
      )}
    </Box>
  );
};
